import Repository from "@/services/Repository";
import { EvaluateFormItem, ItemType } from "../models/EvaluateFormItem";

const resource = "/forms";

const sorter = (a, b) => {
  return parseFloat(a.order) > parseFloat(b.order) ? 1 : -1;
};

const addExpanedToTopic = topic => {
  let desirableAttributes = topic.desirableAttributes
    .map(attribute => {
      return addExpanedToAttribute(attribute, topic._id, topic.studyClass);
    })
    .sort(sorter);

  return new EvaluateFormItem({
    id: topic._id,
    order: topic.order,
    title: topic.title,
    name: `${topic.order}. ${topic.title}`,
    children: desirableAttributes,
    studyClass: topic.studyClass,
    type: ItemType.topic,
    isMastered: topic.isMastered
  });
};

const addExpanedToAttribute = (attr, topicId, studyClass) => {
  let indicators = attr.indicators
    .map(indicator => {
      return addExpanedToIndicator(indicator, attr._id, topicId, studyClass);
    })
    .sort(sorter);

  return new EvaluateFormItem({
    id: attr._id,
    order: attr.order,
    title: attr.title,
    name: `${attr.order}. ${attr.title}`,
    children: indicators,
    studyClass,
    type: ItemType.attribute,
    isMastered: attr.isMastered
  });
};

const addExpanedToIndicator = (ind, attributeId, topicId, studyClass) => {
  let conditions = ind.desirableConditions
    .map(condition => {
      condition.parent = ind;
      return addExpanedToCondition(
        condition,
        ind._id,
        attributeId,
        topicId,
        ind.desirableConditions.length
      );
    })
    .sort(sorter);
  return new EvaluateFormItem({
    id: ind._id,
    order: ind.order,
    title: ind.title,
    name: `${ind.order}. ${ind.title}`,
    type: ItemType.indicator,
    studyClass,
    children: conditions,
    isMastered: ind.isMastered
  });
};

const addExpanedToCondition = (
  condition,
  indicatorId,
  attributeId,
  topicId
) => {
  let score = {
    term1: { time1: 0, time2: 0 },
    term2: { time1: 0, time2: 0 }
  };
  if (condition.score) {
    score = condition.score;
  }

  return new EvaluateFormItem({
    id: condition._id,
    order: condition.order,
    title: condition.title,
    name: `${condition.order}. ${condition.title}`,
    record: condition.recordId,
    score,
    type: ItemType.condition,
    studyClass: condition.studyClass,
    isMastered: condition.isMastered,
    parent: condition.parent,
    indicator: indicatorId,
    desirableAttribute: attributeId,
    desirableCondition: condition._id,
    developmentTopic: topicId
  });
};

export default {
  async fetchMany() {
    let path = "all";
    let result = await Repository.get(`${resource}/${path}`);
    let tempTopics = result.data;
    let usableTopics = tempTopics.map(addExpanedToTopic);
    return usableTopics.sort(sorter);
  },
  async createDefaultForm() {
    let path = "create-forms";
    return Repository.post(`${resource}/${path}`, {});
  },
  async getDevelopmentTopics({ studyClass }) {
    let params = { studyClass };
    let path = "topics";
    return Repository.get(`${resource}/${path}`, { params });
  },
  async postNewTopic(newContent) {
    let path = "development-topics";
    return Repository.post(`${path}`, newContent);
  },
  async postNewIndicator(newContent) {
    let path = "indicators";
    return Repository.post(`${path}`, newContent);
  },
  async postNewAttribute(newContent) {
    let path = "desirable-attributes";
    return Repository.post(`${path}`, newContent);
  },
  async postNewCondition(newContent) {
    let path = "desirable-conditions";
    return Repository.post(`${path}`, newContent);
  },

  async putTopic(content) {
    let path = "development-topics";
    return Repository.put(`${path}/id/${content.id}`, content);
  },
  async putIndicator(content) {
    let path = "inditcators";
    return Repository.put(`${path}/id/${content.id}`, content);
  },
  async putAttribute(content) {
    let path = "desirable-attributes";
    return Repository.put(`${path}/id/${content.id}`, content);
  },
  async putCondition(content) {
    let path = "desirable-conditions";
    return Repository.put(`${path}/id/${content.id}`, content);
  },

  async deleteTopic(contentId) {
    let path = "development-topics";
    return Repository.delete(`${path}/id/${contentId}`);
  },
  async deleteIndicator(contentId) {
    let path = "inditcators";
    return Repository.delete(`${path}/id/${contentId}`);
  },
  async deleteAttribute(contentId) {
    let path = "desirable-attributes";
    return Repository.delete(`${path}/id/${contentId}`);
  },
  async deleteCondition(contentId) {
    let path = "desirable-conditions";
    return Repository.delete(`${path}/id/${contentId}`);
  },

  async getEvaluateForm({ student, schoolYear, studyClass }) {
    let path = "evaluate";
    let params = { schoolYear, studyClass };
    let result = await Repository.get(`${resource}/${path}/${student}`, {
      params
    });
    let tempTopics = result.data;
    let usableTopics = tempTopics.map(addExpanedToTopic);

    return usableTopics.sort(sorter);
  },

  async getEvaluateFormStudyClass({ studyClass, schoolYear }) {
    let path = "evaluate-studyclass";
    let params = { schoolYear, studyClass };
    let result = await Repository.get(`${resource}/${path}`, {
      params
    });
    let tempTopics = result.data;
    let usableTopics = tempTopics.map(addExpanedToTopic);

    return usableTopics.sort(sorter);
  },

  async getEvaluateFormClassroom({ classroom, studyClass, schoolYear }) {
    let path = "evaluate-classroom";

    let params = { classroom, studyClass, schoolYear };
    let result = await Repository.get(`${resource}/${path}`, {
      params
    });
    let studentTopics = result.data;
    studentTopics = studentTopics.map(studentTopic => {
      return {
        student: studentTopic.student,
        topics: studentTopic.topics.map(addExpanedToTopic)
      };
    });

    return studentTopics;
  },

  async putConditionRecord({ recordId, score, schoolYear }) {
    let path = "evaluate";

    return Repository.put(`${resource}/${path}/${recordId}`, {
      score,
      schoolYear
    });
  },
  async postConditionRecord({
    score,
    student,
    indicator,
    classroom,
    schoolYear,
    studyClass,
    developmentTopic,
    desirableAttribute,
    desirableCondition
  }) {
    let path = "evaluate";

    return Repository.post(`${resource}/${path}`, {
      score,
      student,
      classroom,
      indicator,
      schoolYear,
      studyClass,
      developmentTopic,
      desirableAttribute,
      desirableCondition
    });
  },
  async putConditionRecordByRoom({
    conditionId,
    score,
    schoolYear,
    studentId
  }) {
    let path = "evaluate-room";
    return Repository.put(`${resource}/${path}/${studentId}`, {
      conditionId,
      score,
      schoolYear
    });
  },
  async getFormRecordByRoom(query) {
    let path = "evaluate-room";
    const { students, desirableCondition, schoolYear } = query;
    let studentIds = students.map(student => student.id);
    let params = {
      studentIds,
      desirableCondition,
      schoolYear
    };
    return Repository.get(`${resource}/${path}`, {
      params
    });
  },
  async createFormByTerm({ schoolYear, studyClass }) {
    let path = "create-development";
    return Repository.post(`${resource}/${path}`, { schoolYear, studyClass });
  },
  async postBulkRecords(entries) {
    let path = "bulk-records";
    return Repository.post(`${resource}/${path}`, { entries });
  }
};
